import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { DisgustFace, HappyFace, NeutralFace, SadFace, SurpriseFace } from '../../ui/icons/icons';
import { ChartOrderItem } from '../../utils/types/analytics/analytics_types';
import { DashletIconGroup } from './DashletIconGroup';

type EmotionDashletProps = {
  id: string;
  name: string;
  value: string;
  dashletItem: ChartOrderItem;
  onDragStart: (item: ChartOrderItem) => void;
  onDragEnter: (item: ChartOrderItem) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
  fullScreenDetected: boolean;
  setFullScreenDetected: (fullscreen: boolean) => void;
  handleDashletVisibility: (item: ChartOrderItem) => void;
  editMode: boolean;
};

export const EmotionDashlet = ({
  id,
  name,
  value,
  dashletItem,
  onDragStart,
  onDragEnter,
  onDragOver,
  onDrop,
  fullScreenDetected,
  setFullScreenDetected,
  handleDashletVisibility,
  editMode,
}: EmotionDashletProps) => {
  const handleDragStart = () => {
    if (onDragStart) {
      onDragStart(dashletItem);
    }
  };

  const handleDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragEnter) {
      onDragEnter(dashletItem);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event);
    }
  };

  const handleEmoji = () => {
    switch (value) {
      case 'Neutral':
      case '':
        return <NeutralFace />;
      case 'Happy':
        return <HappyFace />;
      case 'Sad':
        return <SadFace />;
      case 'Disgust':
        return <DisgustFace />;
      case 'Surprise':
        return <SurpriseFace />;
      default:
        return <NeutralFace />;
    }
  };

  return (
    <Paper
      variant="outlined"
      className="group"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        minHeight: 180,
        borderRadius: '16px',
        p: 2.5,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
          borderColor: 'primary.main',
        },
      }}
    >
      <DashletIconGroup
        id={id}
        dashletItem={dashletItem}
        setFullScreenDetected={setFullScreenDetected}
        handleDashletVisibility={handleDashletVisibility}
        editMode={editMode}
      />

      <Box
        id={id}
        draggable
        onDragStart={handleDragStart}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          opacity: dashletItem.hidden ? 0.4 : 1,
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: fullScreenDetected ? '2.25rem' : '0.875rem',
            color: 'text.secondary',
            mb: fullScreenDetected ? 3 : 1.5,
            textAlign: 'center',
          }}
        >
          {name}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Box sx={{ fontSize: fullScreenDetected ? '5rem' : '3rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {handleEmoji()}
          </Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: fullScreenDetected ? '2.5rem' : '1.125rem',
              color: 'text.primary',
              textAlign: 'center',
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};
